// Shared by the story pages, the reader and the grading route: what the browser receives (never a correct answer),
// the XP amounts, and the deterministic shuffles of the ordering exercise and the question options.

export const QUESTION_XP = 2
export const ORDER_XP = 5

export interface Speaker {
    pra: string | null
    hi: string
    en: string
}

export interface ReaderLine {
    pra: string
    hi: string
    en: string
    speaker: Speaker | null
}

export interface ReaderQuestion {
    pra: string
    hi: string
    en: string
    /** Options in display order (shuffled with a fixed seed). The answer is graded on the server by its text. */
    options: string[]
}

export interface OrderTile {
    pra: string
    speaker: Speaker | null
}

export interface StoryWord {
    key: string
    /** The dictionary form: root, stem, pronoun base or अव्यय. */
    label: string
    translit: string
    tags: { hi: string, en: string }[]
    meaning: { hi: string, en: string }
    /** The forms of this word that occur in the story. */
    forms: string[]
    /** Headword page, when the word is in the dictionary. */
    href: string | null
}

export interface PreTaughtPhrase {
    form: string
    hi: string
    en: string
    note: { hi: string, en: string }
}

export interface StoryTitle {
    pra: string
    hi: string
    en: string
}

export interface ReaderStory {
    id: string
    kind: 'story' | 'dialogue'
    level: number
    title: StoryTitle
    teachingPoint: { hi: string, en: string } | null
    status: string
    image: string | null
    lines: ReaderLine[]
    questions: ReaderQuestion[]
    /** The story's lines in a shuffled order; the original position is known only to the server. */
    orderTiles: OrderTile[]
    words: StoryWord[]
    preTaught: PreTaughtPhrase[]
}

export interface StoryCardData {
    id: string
    kind: 'story' | 'dialogue'
    level: number
    title: StoryTitle
    teachingPoint: { hi: string, en: string } | null
    lineCount: number
    questionCount: number
    speakers: Speaker[]
    image: string | null
    status: string
    /** Published (or shown in development): learners can read it. */
    visible: boolean
    /** Not visible to learners, but the signed-in editor or reviewer may open it as a preview. */
    preview: boolean
    /** After the free units: opens with a plan or the trial. */
    locked: boolean
}

export interface StoryAttempt {
    ref: string
    skill: string
    correct: boolean
    code?: string
    label?: string
}

export interface StoryGradeResponse {
    kind: 'question' | 'order'
    correct: boolean
    /** The question that was graded. */
    index?: number
    /** Ordering: which positions hold the right line. */
    positions?: boolean[]
    xpAwarded: number
    receipt?: string
    /** Reviewer preview of an unpublished story: graded, but no XP and no attempt recorded. */
    preview: boolean
    attempt: StoryAttempt | null
}

export type StoryGradeRequest =
    | { kind: 'question', index: number, answer: string }
    | { kind: 'order', order: number[] }

/** Image path for a story illustration ("vijjalayam.webp" lives in /public/images/stories). */
export const storyImageSrc = (image: string): string => (image.startsWith('/') || /^https?:\/\//.test(image) ? image : `/images/stories/${image}`)

const seedOf = (text: string): number => {
    let hash = 2166136261

    for (const ch of text) {
        hash ^= ch.codePointAt(0) ?? 0
        hash = Math.imul(hash, 16777619)
    }

    return hash >>> 0
}

/** mulberry32: a tiny deterministic generator, so the server and every visit see the same order. */
const generator = (seed: number): (() => number) => {
    let state = seed

    return () => {
        state = (state + 0x6D2B79F5) | 0

        let t = Math.imul(state ^ (state >>> 15), 1 | state)

        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t

        return ((t ^ (t >>> 14)) >>> 0) / 4294967296
    }
}

const shuffle = (count: number, seedText: string): number[] => {
    const order = Array.from({ length: count }, (_, i) => i)
    const next = generator(seedOf(seedText))

    for (let i = count - 1; i > 0; i -= 1) {
        const j = Math.floor(next() * (i + 1))
        const keep = order[i]

        order[i] = order[j]
        order[j] = keep
    }

    return order
}

/**
 * The ordering exercise: tile i shows line shuffledOrder(id, n)[i]. Never the story's own order
 * (if the shuffle happens to keep it, the first line moves to the end).
 */
export const shuffledOrder = (id: string, count: number): number[] => {
    const order = shuffle(count, `order|${id}`)

    if (count > 1 && order.every((value, i) => value === i)) order.push(order.shift() as number)

    return order
}

/** Display order of a question's options: option i shown is options[optionOrder(...)[i]]. */
export const optionOrder = (id: string, question: number, count: number): number[] => shuffle(count, `options|${id}|${question}`)

/** Ask the server to grade one exercise of a story. Throws with the server's message when it refuses. */
export const postStoryGrade = async (id: string, request: StoryGradeRequest): Promise<StoryGradeResponse> => {
    const res = await fetch(`/api/stories/${encodeURIComponent(id)}/grade`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request)
    })
    const data = await res.json().catch(() => null) as (StoryGradeResponse & { error?: string }) | null

    if (!res.ok || !data) throw new Error(data?.error ?? 'The answer could not be checked.')

    return data
}
